import { useState, useEffect, useRef } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RoleProvider, useRole } from './context/RoleContext';
import { ThemeProvider } from './context/ThemeContext';
import { AccentProvider } from './context/AccentContext';
import { SearchProvider } from './context/SearchContext';
import { NavigationGuardProvider, useNavigationGuardRef } from './context/NavigationGuardContext';
import { I18nProvider, useT } from './i18n';
import { api } from './api/client';
import { ConfirmModal } from './components/ConfirmModal';
import { LoginPage } from 'auth-sso-kit/client';
import { AppShell } from './components/AppShell';
import { RepertoirePage } from './pages/RepertoirePage';
import { ProcessPage } from './pages/ProcessPage';
import { MyWorkPage } from './pages/MyWorkPage';
import { GovernancePage } from './pages/GovernancePage';
import { ObservationPage } from './pages/ObservationPage';
import { TicketsPage } from './pages/TicketsPage';
import { AdminPage } from './pages/AdminPage';
import { CdeProcessPage } from './pages/CdeProcessPage';

export type Page =
  | 'home' | 'repository' | 'process' | 'governance' | 'observe'
  | 'tickets' | 'admin' | 'cde-process' | 'profile';

/** Pages hidden (not greyed) from roles that have no business there. */
const PAGE_ROLES: Partial<Record<Page, string[]>> = {
  home: ['admin', 'cde'],
  governance: ['admin', 'cde', 'governance'],
  observe: ['admin', 'cde', 'governance'],
  admin: ['admin'],
};

/** Landing screen per role — the first thing each user actually needs. */
const LANDING: Record<string, Page> = {
  cde: 'home', admin: 'repository', governance: 'governance', user: 'repository',
};

function parseHash(): { page: Page; id: number | null } {
  const h = window.location.hash.replace(/^#\/?/, '');
  const [name, id] = h.split('?')[0].split('/');   // #/admin?tab=repo — the query belongs to the page
  const page = (name || '') as Page;
  return { page, id: id ? Number(id) : null };
}

function AppContent() {
  const { user, loading } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const { t, setLang } = useT();
  const [page, setPage] = useState<Page | null>(null);
  const [processId, setProcessId] = useState<number | null>(null);
  const [repoPath, setRepoPath] = useState<string | null>(null);
  const isDirtyRef = useNavigationGuardRef();
  const prevHashRef = useRef(window.location.hash);
  const bypassRef = useRef(false);
  const [pendingHash, setPendingHash] = useState<string | null>(null);

  // server-stored language wins over localStorage
  useEffect(() => {
    if (!user) return;
    api.getProfile().then(p => { if (p.lang === 'fr' || p.lang === 'en') setLang(p.lang); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Hash routing with the dirty guard: an in-app navigation away from unsaved
  // work reverts the URL and asks first (same mechanism as PMO's App.tsx).
  useEffect(() => {
    // Wait for BOTH the account and the role: RoleProvider reports "loaded"
    // with role=user while the account is still being fetched, and routing on
    // that bounced every reload of #/admin or #/governance to the répertoire.
    if (loading || roleLoading) return;
    const apply = () => {
      const { page: target, id } = parseHash();
      const allowed = PAGE_ROLES[target];
      if (!target || (allowed && !allowed.includes(role))) {
        const fallback = LANDING[role] ?? 'repository';
        window.location.hash = `#/${fallback}`;
        return;
      }
      if (target === 'process') {
        // #/process/12 (cycle record) or #/process/file?path=… (documented file)
        const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
        const pid = id && !isNaN(id) ? id : null;
        const path = params.get('path');
        if (!pid && !path) { window.location.hash = `#/${LANDING[role] ?? 'repository'}`; return; }
        setProcessId(pid);
        setRepoPath(path);
      }
      setPage(target);
      prevHashRef.current = window.location.hash;
    };

    const onHashChange = () => {
      if (bypassRef.current) { bypassRef.current = false; apply(); return; }
      if (isDirtyRef.current && window.location.hash !== prevHashRef.current) {
        const wanted = window.location.hash;
        history.replaceState(null, '', prevHashRef.current);
        setPendingHash(wanted);
        return;
      }
      apply();
    };

    if (!window.location.hash) window.location.hash = `#/${LANDING[role] ?? 'repository'}`;
    apply();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [role, roleLoading, loading]);

  if (loading || roleLoading) return <div className="page-loading"><div className="spinner" /></div>;
  if (!user) return <LoginPage title="CdE" subtitle={t('shell.title.repository')} />;
  if (!page) return <div className="page-loading"><div className="spinner" /></div>;

  return (
    <AppShell currentPage={page} onNavigate={(p) => { window.location.hash = `#/${p}`; }}>
      {page === 'home' && <MyWorkPage />}
      {page === 'repository' && <RepertoirePage />}
      {page === 'process' && <ProcessPage processId={processId} repoPath={repoPath} />}
      {page === 'governance' && <GovernancePage />}
      {page === 'observe' && <ObservationPage />}
      {page === 'tickets' && <TicketsPage />}
      {page === 'admin' && <AdminPage />}
      {page === 'cde-process' && <CdeProcessPage />}
      {page === 'profile' && <RepertoirePage />}

      <ConfirmModal
        open={pendingHash != null}
        title={t('shell.unsavedTitle')}
        message={t('shell.unsavedMessage')}
        confirmLabel={t('shell.unsavedConfirm')}
        destructive
        onConfirm={() => {
          if (pendingHash) { bypassRef.current = true; isDirtyRef.current = false; window.location.hash = pendingHash; }
          setPendingHash(null);
        }}
        onCancel={() => setPendingHash(null)}
      />
    </AppShell>
  );
}

export function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <RoleProvider>
          <NavigationGuardProvider>
            <ThemeProvider>
              <SearchProvider>
                <AccentProvider>
                  <AppContent />
                </AccentProvider>
              </SearchProvider>
            </ThemeProvider>
          </NavigationGuardProvider>
        </RoleProvider>
      </AuthProvider>
    </I18nProvider>
  );
}
